import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { de } from "./translations/de";
import { fr } from "./translations/fr";
import { en } from "./translations/en";

export type Language = "de" | "fr" | "en";

type Translations = typeof de;

const SUPPORTED: Language[] = ["de", "fr", "en"];

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const translations: Record<Language, Translations> = { de, fr, en };

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function getInitialLanguage(): Language {
  if (typeof window === "undefined") return "de";
  const param = new URLSearchParams(window.location.search).get("lang");
  if (param && SUPPORTED.includes(param as Language)) return param as Language;
  const stored = window.localStorage.getItem("lang");
  if (stored && SUPPORTED.includes(stored as Language)) return stored as Language;
  return "de";
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (typeof window === "undefined") return;
    window.localStorage.setItem("lang", lang);
    const url = new URL(window.location.href);
    if (lang === "de") {
      url.searchParams.delete("lang");
    } else {
      url.searchParams.set("lang", lang);
    }
    window.history.replaceState({}, "", url);
  };

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
}
